import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { calculateEMI } from '@/lib/api';
import { Calculator, IndianRupee, Percent, Calendar, TrendingUp, PieChart } from 'lucide-react';
import Seo from '@/components/Seo';

const EMICalculatorPage = () => {
  const [searchParams] = useSearchParams();
  const [principal, setPrincipal] = useState(searchParams.get('amount') || searchParams.get('price') || '5000000');
  const [interestRate, setInterestRate] = useState('8.5');
  const [tenure, setTenure] = useState('240'); // 20 years in months
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (principal && interestRate && tenure) {
      handleCalculate();
    }
  }, []);

  const handleCalculate = async () => {
    if (!principal || !interestRate || !tenure) return;

    setLoading(true);
    try {
      const data = await calculateEMI({
        principal: parseFloat(principal),
        interest_rate: parseFloat(interestRate),
        tenure_months: parseInt(tenure)
      });
      setResult(data);
    } catch (error) {
      console.error('EMI calculation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} Lac`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const tenureYears = Math.round(parseInt(tenure || 0) / 12);

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16">
      <Seo title="Home Loan EMI Calculator" description="Calculate your home loan EMI with our smart calculator. Enter principal, interest rate, and tenure to see monthly EMI, total interest, and amortisation." path="/emi-calculator" />
      <div className="container-luxury">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="caption mb-3">Financial Planning</p>
          <h1 className="heading-secondary text-[#0F172A] mb-4">EMI Calculator</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Plan your home loan with our easy-to-use EMI calculator. Get instant estimates for your monthly payments.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Calculator Form */}
            <div className="bg-white p-8 rounded-sm shadow-sm border border-border/50">
              <h2 className="font-['Plus_Jakarta_Sans'] text-xl font-semibold text-[#0F172A] mb-8">Calculate Your EMI</h2>

              <div className="space-y-8">
                {/* Loan Amount */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <Label className="flex items-center gap-2">
                      <IndianRupee className="h-4 w-4 text-[#D4AF37]" />
                      Loan Amount
                    </Label>
                    <span className="font-semibold text-[#0F172A]">{formatCurrency(parseFloat(principal || 0))}</span>
                  </div>
                  <Input
                    type="number"
                    value={principal}
                    onChange={(e) => setPrincipal(e.target.value)}
                    placeholder="Enter loan amount"
                    className="mb-4"
                    data-testid="loan-amount"
                  />
                  <Slider
                    value={[parseFloat(principal || 0)]}
                    onValueChange={([value]) => setPrincipal(value.toString())}
                    max={100000000}
                    min={100000}
                    step={100000}
                    className="[&_[role=slider]]:bg-[#D4AF37]"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-2">
                    <span>₹1 Lac</span>
                    <span>₹10 Cr</span>
                  </div>
                </div>

                {/* Interest Rate */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <Label className="flex items-center gap-2">
                      <Percent className="h-4 w-4 text-[#D4AF37]" />
                      Interest Rate (% p.a.)
                    </Label>
                    <span className="font-semibold text-[#0F172A]">{interestRate}%</span>
                  </div>
                  <Input
                    type="number"
                    step="0.1"
                    value={interestRate}
                    onChange={(e) => setInterestRate(e.target.value)}
                    placeholder="Enter interest rate"
                    className="mb-4"
                    data-testid="interest-rate"
                  />
                  <Slider
                    value={[parseFloat(interestRate || 0)]}
                    onValueChange={([value]) => setInterestRate(value.toFixed(1))}
                    max={20}
                    min={5}
                    step={0.1}
                    className="[&_[role=slider]]:bg-[#D4AF37]"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-2">
                    <span>5%</span>
                    <span>20%</span>
                  </div>
                </div>

                {/* Loan Tenure */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <Label className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-[#D4AF37]" />
                      Loan Tenure
                    </Label>
                    <span className="font-semibold text-[#0F172A]">{tenureYears} Years ({tenure} months)</span>
                  </div>
                  <Input
                    type="number"
                    value={tenure}
                    onChange={(e) => setTenure(e.target.value)}
                    placeholder="Enter tenure in months"
                    className="mb-4"
                    data-testid="loan-tenure"
                  />
                  <Slider
                    value={[parseInt(tenure || 0)]}
                    onValueChange={([value]) => setTenure(value.toString())}
                    max={360}
                    min={12}
                    step={12}
                    className="[&_[role=slider]]:bg-[#D4AF37]"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-2">
                    <span>1 Year</span>
                    <span>30 Years</span>
                  </div>
                </div>

                <Button 
                  onClick={handleCalculate} 
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="calculate-emi-btn"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  {loading ? 'Calculating...' : 'Calculate EMI'}
                </Button>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-6">
              {/* EMI Result Card */}
              <div className="bg-[#0F172A] p-8 rounded-sm text-white">
                <h3 className="text-sm uppercase tracking-widest text-white/70 mb-2">Monthly EMI</h3>
                <p className="font-['Plus_Jakarta_Sans'] text-5xl font-bold text-[#D4AF37]" data-testid="emi-result">
                  {result ? `₹${result.emi.toLocaleString('en-IN')}` : '₹0'}
                </p>
                <p className="text-white/70 mt-2">per month</p>
              </div>

              {/* Breakdown */}
              {result && (
                <>
                  <div className="bg-white p-8 rounded-sm shadow-sm border border-border/50">
                    <h3 className="font-medium text-[#0F172A] mb-6">Loan Breakdown</h3>
                    
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-3 border-b">
                        <span className="text-muted-foreground">Principal Amount</span>
                        <span className="font-semibold text-[#0F172A]" data-testid="principal-amount">
                          {formatCurrency(result.principal)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b">
                        <span className="text-muted-foreground">Total Interest</span>
                        <span className="font-semibold text-red-500" data-testid="total-interest">
                          {formatCurrency(result.total_interest)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-3">
                        <span className="text-muted-foreground">Total Amount Payable</span>
                        <span className="font-semibold text-[#0F172A] text-lg" data-testid="total-amount">
                          {formatCurrency(result.total_amount)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Visual Breakdown */}
                  <div className="bg-white p-8 rounded-sm shadow-sm border border-border/50">
                    <h3 className="font-medium text-[#0F172A] mb-6">Payment Distribution</h3>
                    
                    <div className="relative h-8 bg-[#F1F5F9] rounded-full overflow-hidden">
                      <div
                        className="absolute left-0 top-0 h-full bg-[#0F172A] rounded-l-full"
                        style={{ width: `${(result.principal / result.total_amount) * 100}%` }}
                      />
                      <div
                        className="absolute right-0 top-0 h-full bg-[#D4AF37] rounded-r-full"
                        style={{ width: `${(result.total_interest / result.total_amount) * 100}%` }}
                      />
                    </div>
                    
                    <div className="flex justify-between mt-4">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-[#0F172A]" />
                        <span className="text-sm text-muted-foreground">
                          Principal ({Math.round((result.principal / result.total_amount) * 100)}%)
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-[#D4AF37]" />
                        <span className="text-sm text-muted-foreground">
                          Interest ({Math.round((result.total_interest / result.total_amount) * 100)}%)
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Tips */}
              <div className="bg-[#F9F9F7] p-6 rounded-sm border border-border/50">
                <h4 className="font-medium text-[#0F172A] mb-4 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-[#D4AF37]" />
                  Tips to Reduce Your EMI
                </h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Increase your down payment to reduce the loan principal</li>
                  <li>• Compare interest rates from multiple banks</li>
                  <li>• Choose a longer tenure for lower EMI (but higher total interest)</li>
                  <li>• Maintain a good credit score for better rates</li>
                  <li>• Consider prepayment options to reduce interest burden</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EMICalculatorPage;
